import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import { isEmpty } from 'lodash';
import {
  getSavedOrderForCart,
  updateOrderBaggageApi,
} from '../../Checkout/CheckoutApis';
import { RootState } from '../../../store/store';
import useGetFlightData from '../../Checkout/Hooks/useGetFlightData';
import { MainProductType } from '../interfaces';
import {
  applyOrderProductsFromApi,
  CommissionType,
  getFlightsFromProducts,
  handleAddBaggageToShoppingCart,
  setWholeCart,
} from '../ShoppingCartSliceV2';
import { ShoppingCartV2 as ShoppingCartV2Type } from '../ShoppingCartSliceV2';
import { Product } from '../interfaces';
import useShoppingCartNotification, {
  NotificationType,
} from './useShoppingCartNotification';

// saved orders: the suppliers whose bags can still change before the flight is booked (MS updateOrderBaggage)
const SAVED_BAGGAGE_SUPPLIERS = ['1', '2'];
const NOT_BOOKED_STATUSES = ['new', 'validated', 'validation_failed', 'not_available', 'not_approved'];

// the supplier (ts) of a cart line - its ts_type, else the one in its identifier (a fresh cart line)
const supplierOf = (item: any): string | null => {
  if (item?.ts_type != null && item.ts_type !== '') return String(item.ts_type);
  try {
    const encoded = String(item?.identifier ?? '').split('::')[0];
    if (!encoded) return null;
    const decoded = JSON.parse(window.atob(encoded));
    const ts = decoded?.ts ?? decoded?.ts_type;
    return ts != null ? String(ts) : null;
  } catch (e) {
    return null;
  }
};

// GET /ms/booking/order answers with the order cart itself (only an error is wrapped - {status: 'error', data})
const cartOfOrderResponse = (res: any): any => {
  const body = res?.data;
  if (!body || body.status === 'error') return null;
  if (Array.isArray(body.products)) return body;
  if (Array.isArray(body.data?.products)) return body.data;
  return null;
};

// the first flight product (flight package / one-way flights / flight) in a cart product's tree
const findFlightContainer = (item: any): any => {
  if (!item) return null;
  const flightTypes = [
    MainProductType.Flights,
    MainProductType.oneWayFlights,
    MainProductType.oneWayFlight,
    MainProductType.Flight,
  ];
  if (flightTypes.includes(item.type)) return item;
  for (const sub of item.subProducts ?? []) {
    const found = findFlightContainer(sub);
    if (found) return found;
  }
  return null;
};

const useHandleManageBaggage = ({product, isTopLevel}: {product: Product, isTopLevel?: boolean}) => {
  const dispatch = useDispatch();
  const createNotification = useShoppingCartNotification();
  const { agent_commission, ShoppingCartV2, attachToOrder } = useSelector(
    (data: RootState) => data.shoppingCartV2
  );
  const { shoppingCartTranslations: t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const { agentInfo } = useSelector((data: RootState) => data.config);
  const [isSavingSaved, setIsSavingSaved] = useState(false);

  let flightPackageIndexInsideDynamicPackage: number | null = null;
  let staticI = ShoppingCartV2.products.findIndex((p) => {
    return p.identifier === product.identifier;
  });

  if (!isTopLevel && product.type === MainProductType.Flights) {
    staticI =
      ShoppingCartV2?.products?.findIndex((p) => {
        return p?.subProducts?.some((subProduct, index) => {
          if (subProduct.identifier === product.identifier) {
            flightPackageIndexInsideDynamicPackage = index;
            return true;
          }
          return false;
        });
      }) ?? -1;
  }

  const { flightBackBaggage, flightFromBaggage } = useGetFlightData({
    flightSubProduct: product.subProducts?.filter(
      (subProduct) =>
        subProduct.type === MainProductType.Flight ||
        subProduct.type === MainProductType.oneWayFlight
    ),
    relatedGuestIndex: product.related_guest_indexes[0],
    guests: ShoppingCartV2.guests,
  });

  const isRoundTrip =
    flightFromBaggage?.is_round_trip || flightBackBaggage?.is_round_trip;

  const handleAddBaggage = (
    e: any,
    direction: number,
    type: MainProductType.Trolly | MainProductType.Suitcase,
    price: number,
    rgi: number
  ) => {

    const isOneWayFlight =
      product.subProducts?.[0]?.type === MainProductType.oneWayFlight;

    const adjustedPrice =
      agent_commission?.type === CommissionType.Percentage
        ? price / (1 - Number(agent_commission?.percentage ?? 0) / 100)
        : price;

    dispatch(
      handleAddBaggageToShoppingCart({
        e,
        direction,
        type,
        staticI,
        relatedGuestIndex: rgi,
        price: adjustedPrice,
        netPrice: price,
        isRoundTrip: isRoundTrip,
        isOneWayFlight,
      })
    );
  };

  const flightFromSuitcaseSaleAllowed = flightFromBaggage?.enable_sale_suitcase;
  const flightFromTrollySaleAllowed = flightFromBaggage?.enable_sale_trolly;
  const flightBackSuitcaseSaleAllowed = flightBackBaggage?.enable_sale_suitcase;
  const flightBackTrollySaleAllowed = flightBackBaggage?.enable_sale_trolly;

  // A product already saved in an order (it has an id): its bags are saved on the order through
  // /ms/booking/order/update-baggage - only for ALP / Sky360 flights not booked with the supplier yet (the bags
  // reach the supplier inside the booking). The new order price needs the customer's approval (an agent's change)
  // or is approved by the customer making it.
  // The bags of a product saved in an order are saved on the order (/ms/booking/order/update-baggage), not just in
  // the cart: a product loaded from the order (it has an id - manage order / a loaded order), or any product of a
  // cart the agent already saved as an order / offer (the cart has the order id + secret, its lines no ids yet).
  // Attaching products to an order: only its existing products are saved - the new ones are still a cart.
  const hasSavedOrder =
    !!ShoppingCartV2?.orderSecret &&
    !!ShoppingCartV2?.orderId &&
    ShoppingCartV2.orderId !== 'ORDER_ID';
  const isSavedProduct = !!product?.id || (hasSavedOrder && !attachToOrder);
  const currentLegs = getFlightsFromProducts(ShoppingCartV2, staticI) ?? [];
  // the flight product the legs belong to (a package / dynamic package holds it as a sub-product) - a fresh cart's
  // legs carry no supplier, their flight product's identifier does
  const flightContainer = findFlightContainer(ShoppingCartV2?.products?.[staticI]);
  // only ALP / Sky360 flights not booked with the supplier yet (a fresh offer's lines carry no status - the server
  // checks it again)
  const canEditSavedBaggage =
    isSavedProduct &&
    currentLegs.length > 0 &&
    currentLegs.every(
      (leg: any) =>
        SAVED_BAGGAGE_SUPPLIERS.includes(
          supplierOf(leg) ?? supplierOf(flightContainer) ?? supplierOf(product) ?? ''
        ) &&
        (!(leg.product_status ?? product.product_status) ||
          NOT_BOOKED_STATUSES.includes(leg.product_status ?? product.product_status))
    );
  const isShowManageBaggageButton =
    (!isSavedProduct || canEditSavedBaggage) &&
    (flightFromSuitcaseSaleAllowed ||
      flightFromTrollySaleAllowed ||
      flightBackSuitcaseSaleAllowed ||
      flightBackTrollySaleAllowed);

  const isCustomer = isEmpty(agentInfo);

  /**
   * Save the bags changed in the panel on the saved order (after a confirmation that says the price changes and
   * who approves it), then load the order again into the cart. prevCart - the cart when the panel opened.
   */
  const saveSavedOrderBaggage = async (
    prevCart: ShoppingCartV2Type | undefined,
    onDone: () => void
  ) => {
    const orderSecret = ShoppingCartV2?.orderSecret;
    if (!orderSecret) {
      onDone();
      return;
    }
    const restore = (message: string) => {
      if (prevCart) dispatch(setWholeCart(prevCart));
      Modal.error({
        title: t.savedBaggageTitle,
        content: t.savedBaggageError.replace('{error}', message),
      });
      onDone();
    };

    // the order as saved - its leg ids and bags are the truth (a just-saved offer's cart lines have no ids)
    let serverCart: any;
    try {
      serverCart = cartOfOrderResponse(await getSavedOrderForCart(orderSecret));
    } catch (error: any) {
      serverCart = null;
    }
    const topProduct = ShoppingCartV2?.products?.[staticI];
    let serverIndex =
      serverCart?.products?.findIndex(
        (p: any) =>
          (topProduct?.id && p.id === topProduct.id) ||
          (topProduct?.identifier && p.identifier === topProduct.identifier)
      ) ?? -1;
    // no id / identifier match (a just-saved cart line) - the order's only product of the same type
    if (serverIndex < 0 && serverCart?.products?.length) {
      const sameType = serverCart.products
        .map((p: any, index: number) => ({ p, index }))
        .filter(({ p }: any) => p.type === topProduct?.type);
      if (sameType.length === 1) serverIndex = sameType[0].index;
    }
    const serverLegs = serverIndex >= 0 ? getFlightsFromProducts(serverCart, serverIndex) ?? [] : [];
    if (!serverLegs.length) {
      restore(t.savedBaggageNotFound);
      return;
    }

    const bagsOf = (leg: any, type: string) =>
      [...(leg?.subProducts?.find((sub: any) => sub.type === type)?.related_guest_indexes ?? [])]
        .map(Number)
        .sort((a, b) => a - b);
    const isOutbound = (leg: any) =>
      !!(leg?.product_data?.flightFrom ?? leg?.product_data?.flightsInfo?.flightFrom);
    const isReturn = (leg: any) =>
      !isOutbound(leg) &&
      !!(leg?.product_data?.flightBack ?? leg?.product_data?.flightsInfo?.flightBack);
    // the server leg of a cart leg - by direction (outbound / return), else by position (one-way legs)
    const serverLegOf = (leg: any, index: number) =>
      (leg?.id && serverLegs.find((serverLeg: any) => serverLeg.id === leg.id)) ||
      (isOutbound(leg) && serverLegs.find(isOutbound)) ||
      (isReturn(leg) && serverLegs.find(isReturn)) ||
      serverLegs[index];

    // a round trip bag (ALP: one price for both legs) is saved under the outbound leg only - the server shows it on
    // both; per-leg bags are saved on each leg
    const outboundIndex = Math.max(0, currentLegs.findIndex(isOutbound));
    const legIndexes = isRoundTrip ? [outboundIndex] : currentLegs.map((_: any, index: number) => index);
    const changes: { flight_order_product_id: number; type: 'suitcase' | 'trolly'; related_guest_indexes: number[] }[] = [];
    legIndexes.forEach((index: number) => {
      const leg = currentLegs[index];
      const serverLeg = serverLegOf(leg, index);
      if (!serverLeg?.id) return;
      [MainProductType.Suitcase, MainProductType.Trolly].forEach((type) => {
        const wanted = bagsOf(leg, type);
        if (JSON.stringify(wanted) !== JSON.stringify(bagsOf(serverLeg, type))) {
          changes.push({
            flight_order_product_id: Number(serverLeg.id),
            type: type as 'suitcase' | 'trolly',
            related_guest_indexes: wanted,
          });
        }
      });
    });
    if (!changes.length) {
      onDone();
      return;
    }

    Modal.confirm({
      title: t.savedBaggageTitle,
      content: isCustomer ? t.savedBaggageCustomerText : t.savedBaggageAgentText,
      okText: t.savedBaggageConfirm,
      cancelText: t.savedBaggageCancel,
      centered: true,
      onOk: async () => {
        setIsSavingSaved(true);
        let lastResult: any = null;
        // every changed leg / bag type is its own call - the price difference is their sum
        let totalDifference = 0;
        try {
          for (const change of changes) {
            const res = await updateOrderBaggageApi({ order_secret: orderSecret, ...change });
            if (res?.data?.status !== 'success') {
              throw new Error(res?.data?.data?.message || res?.data?.message || 'error');
            }
            lastResult = res.data.data;
            totalDifference += Number(res.data.data?.price_difference ?? 0);
          }
          const savedCart = cartOfOrderResponse(await getSavedOrderForCart(orderSecret));
          if (savedCart) {
            dispatch(setWholeCart({ ...savedCart, isSaved: true }));
            dispatch(applyOrderProductsFromApi(savedCart.products));
          }
          const currency = lastResult?.currency_sign ?? '';
          const difference = Math.round(totalDifference * 100) / 100;
          createNotification({
            message: isCustomer
              ? t.savedBaggageDoneCustomer.replace(
                  '{total}',
                  `${Number(lastResult?.price_total ?? 0).toFixed(2)} ${currency}`
                )
              : t.savedBaggageDoneAgent.replace(
                  '{amount}',
                  `${difference > 0 ? '+' : ''}${difference.toFixed(2)} ${currency}`
                ),
            // the agent's change waits for the customer's approval
            type: isCustomer ? NotificationType.Success : NotificationType.Warning,
          });
          onDone();
        } catch (error: any) {
          // back to the bags as they were
          restore(error?.message ?? '');
        } finally {
          setIsSavingSaved(false);
        }
      },
    });
  };

  return {
    handleAddBaggage,
    notifyBaggageSaved: createNotification,
    isSavedProduct,
    saveSavedOrderBaggage,
    isSavingSaved,
    isRoundTrip,
    flightBackBaggage,
    flightFromBaggage,
    isShowManageBaggageButton,
  };
};

export default useHandleManageBaggage;
